import React, { useState } from "react";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";

import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import {
  Asset,
  Report,
  AssetAppeal,
  AssetAppealRequestActionStatusEnum,
} from "types";
import {
  addAppealReport,
  getRespondentAssetAppealById,
  updateRespondentAssetAppeal,
} from "services/assetAppealService";
import Select, { MultiValue } from "react-select";

interface SelectOption {
  value: string | number;
  label: string;
}
interface FinalReportProps {
  appealId?: string;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: () => void;
  appeal: AssetAppeal | null;
  assets?: Asset[];
  reports?: Report[];
  recoveryReports?: Report[];
  assetsNeedingReport: Asset[]; // This now contains only assets that need final reports
  setAppeal?: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
}

export const AssetRespondentAppealFinalReport: React.FC<FinalReportProps> = ({
  appealId,
  onNewDataAdded,
  setActiveTab,
  appeal,
  assets,
  reports = [],
  recoveryReports = [],
  assetsNeedingReport, // Use this instead of all assets
  setAppeal,
}) => {
  const [formData, setFormData] = useState<{
    court_decision_date: string;
    court_decision_summary: string;
    prosecutor_feedback_on_prohabition_decision: string;
    asset: string[];
  }>({
    court_decision_date: "",
    court_decision_summary: "",
    prosecutor_feedback_on_prohabition_decision: "",
    asset: [],
  });

  const [submitting, setSubmitting] = useState(false);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string | string[] | boolean } },
  ) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleAddFinalReport = async () => {
    // ---- basic validation ----
    if (!formData.court_decision_summary.trim()) {
      showTooltip(t("court_decision_summary_is_required"), "error");
      return;
    }
    if (!formData.court_decision_date) {
      showTooltip(t("court_decision_date_is_required"), "error");
      return;
    }
    if (assetsNeedingReport.length > 0 && formData.asset.length === 0) {
      showTooltip(t("please_select_at_least_one_asset"), "error");
      return;
    }

    const data = new FormData();
    if (appeal?.appeal_type === "appeal_by_respondant") {
      data.append("asset_respondent_appeal", appealId as string);
    } else data.append("asset_appeal_request", appealId as string);

    data.append("court_decision_date", formData.court_decision_date);
    data.append("court_decision_summary", formData.court_decision_summary);
    data.append(
      "prosecutor_feedback_on_prohabition_decision",
      formData.prosecutor_feedback_on_prohabition_decision || "",
    );
    data.append("is_next_appointment_reason_final_verdict", "true");

    // Use selected assets (only from assetsNeedingReport)
    if (formData.asset && formData.asset.length > 0) {
      formData.asset.forEach((id) => data.append("asset", id));
    } else if (assetsNeedingReport.length === 1) {
      // fallback only if a single asset needs report
      data.append("asset", assetsNeedingReport[0].id as string);
    }

    uploadedFiles.forEach((f) => data.append("court_decision_documents", f));

    try {
      setSubmitting(true);

      let savedReport;
      if (
        appeal?.action_status ===
        AssetAppealRequestActionStatusEnum?.APPEAL_APPROVED
      ) {
        savedReport = await addAppealReport(data);
      }

      if (savedReport) {
        onNewDataAdded?.();
        const newStatus =
          appeal?.action_status ===
          AssetAppealRequestActionStatusEnum?.APPEAL_APPROVED
            ? AssetAppealRequestActionStatusEnum?.APPEAL_FINAL_DECISION
            : "";

        const response = await updateRespondentAssetAppeal(appealId as string, {
          action_status: newStatus,
        });
        if (setAppeal && response) {
          const data = await getRespondentAssetAppealById(appealId as string);
          if (data) {
            setAppeal(data);
            setActiveTab?.("Report");
          }
        }

        showTooltip(t("final_report_submitted_successfully"), "success");

        setFormData({
          court_decision_date: "",
          court_decision_summary: "",
          prosecutor_feedback_on_prohabition_decision: "",
          asset: [],
        });
        setUploadedFiles([]);
      }
    } catch (err) {
      console.error(err);
      showTooltip(t("failed_to_submit_final_report"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <p className="text-sm mb-2 text-gray-400">Fill Final Report</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAddFinalReport();
        }}
      >
        <div className="flex flex-col mb-4 mt-2">
          {assetsNeedingReport.length > 0 && (
            <div className="flex flex-col">
              <label className="referral-form-label">
                Select Assets for Final Report
              </label>
              <Select<SelectOption, true>
                className="asset-form-field p-5 border border-gray-300 rounded-md"
                classNamePrefix="rs"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: "none",
                    padding: 0,
                    background: "transparent",
                    margin: "0",
                    color: "black",
                    minHeight: "44px",
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? "#3b82f6"
                      : state.isFocused
                        ? "#f3f4f6"
                        : "white",
                    color: state.isSelected ? "white" : "#212121",
                    fontSize: "14px",
                    cursor: "pointer",
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: "#e5e7eb",
                    borderRadius: "6px",
                  }),
                  multiValueLabel: (provided) => ({
                    ...provided,
                    color: "#374151",
                    fontSize: "14px",
                  }),
                }}
                isMulti
                options={assetsNeedingReport?.map((asset) => ({
                  value: asset.id as string,
                  label: asset.asset_unique_id
                    ? asset.asset_unique_id
                    : String(asset.id),
                }))}
                value={
                  formData.asset
                    ? assetsNeedingReport
                        .filter((asset) =>
                          formData.asset.includes(asset.id as string),
                        )
                        .map((asset) => ({
                          value: asset.id as string,
                          label: asset.asset_unique_id
                            ? asset.asset_unique_id
                            : String(asset.id),
                        }))
                    : []
                }
                onChange={(selectedOptions: MultiValue<SelectOption>) => {
                  const selectedValues = selectedOptions.map(
                    (option) => option.value,
                  );
                  handleInputChange({
                    target: {
                      name: "asset",
                      value: selectedValues as string[],
                    },
                  });
                }}
                placeholder="Choose assets for final report..."
                noOptionsMessage={() => "No assets available"}
              />
            </div>
          )}

          {assetsNeedingReport.length === 0 && (
            <p className="text-green-600">All assets have final reports</p>
          )}
        </div>

        {/* Rest of the form remains the same */}
        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">Court Decision Summary</label>
          <textarea
            className="referral-form-field min-h-[100px]"
            name="court_decision_summary"
            placeholder="detail here..."
            value={formData.court_decision_summary}
            onChange={handleInputChange}
          />
        </div>

        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">Court Decision Date</label>
          <input
            type="date"
            className="referral-form-field"
            name="court_decision_date"
            value={formData.court_decision_date}
            onChange={handleInputChange}
          />
        </div>

        <div className="flex flex-col mb-4">
          <MultipleFileUploadComponent
            onFilesChange={setUploadedFiles}
            uploadedFiles={uploadedFiles}
            label="Report Doc"
          />
        </div>

        <div className="flex flex-col mb-6">
          <label className="referral-popup-label">Prosecutor Feedback</label>
          <textarea
            className="referral-form-field min-h-[100px]"
            name="prosecutor_feedback_on_prohabition_decision"
            placeholder="Enter prosecutor feedback..."
            value={formData.prosecutor_feedback_on_prohabition_decision}
            onChange={handleInputChange}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={
              submitting ||
              !formData.court_decision_summary.trim() ||
              !formData.court_decision_date ||
              formData.asset.length === 0
            }
            className="referral-form-button text-white px-8 py-3"
          >
            {submitting ? "Submitting..." : "Set Court Decision"}
          </button>
        </div>
      </form>
    </div>
  );
};
